import * as React from "react";
import { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { Input, FormGroup, ControlLabel } from "components/bootstrap";
import { Select } from "components/common";
import AppConfig from "util/AppConfig";
import * as FormsUtils from "util/FormsUtils";
import AnomalyQueryInput from "security-app/components/AnomalyEvents/AnomalyQueryInput";
export const initialThresholdedRcfConfig = {
  type: "thresholded_rcf",
  feature_field: "",
  feature_function: "MAX",
  shingle_size: 1,
  number_of_trees: 100,
  sample_size: 256,
  grade_threshold: 0,
  score_threshold: 0,
  create_non_anomaly_events: false,
  category_field: null,
  query: null
};
const FEATURE_FUNCTION_OPTIONS = [
  { value: "MAX", label: "Maximum" },
  { value: "MIN", label: "Minimum" },
  { value: "AVG", label: "Average" },
  { value: "SUM", label: "Sum" },
  { value: "COUNT", label: "Count" },
  { value: "CARD", label: "Cardinality" }
];
const RcfEventForm = ({ eventDefinition, onChange, validation: _validation, section = "config" }) => {
  var _a, _b, _c, _d, _e, _f;
  const [currentConfig, setCurrentConfig] = useState(() => {
    var _a2;
    return {
      ...eventDefinition.config,
      config: (_a2 = eventDefinition.config.config) != null ? _a2 : initialThresholdedRcfConfig
    };
  });
  const subtypeConfig = (_a = currentConfig.config) != null ? _a : initialThresholdedRcfConfig;
  const isFieldLocked = ((_b = AppConfig.pluginUISettings("org.graylog.plugins.securityapp")) == null ? void 0 : _b.enable_full_rcf_definition_edit) !== true;
  const isIlluminate = eventDefinition._scope === "ILLUMINATE";
  const setCurrentConfigAndPropagateChange = (config) => {
    setCurrentConfig(config);
    onChange("config", config);
  };
  const updateSubtypeConfig = (name, value) => {
    var _a2;
    const config = cloneDeep(eventDefinition.config);
    const nextSubtypeConfig = cloneDeep((_a2 = config.config) != null ? _a2 : subtypeConfig);
    nextSubtypeConfig[name] = value;
    config.config = nextSubtypeConfig;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleSubtypeInputChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const nullableFields = ["category_field", "query"];
    const normalizedValue = nullableFields.includes(name) && value === "" ? null : value;
    updateSubtypeConfig(name, normalizedValue);
  };
  const handleFeatureFunctionChange = (nextValue) => {
    updateSubtypeConfig("feature_function", nextValue != null ? nextValue : FEATURE_FUNCTION_OPTIONS[0].value);
  };
  const handleQueryChange = (changeEvent) => {
    const value = changeEvent.target.value === "" ? null : changeEvent.target.value;
    updateSubtypeConfig("query", value);
    return Promise.resolve(changeEvent.target.value);
  };
  if (section === "query") {
    if (isFieldLocked || isIlluminate) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(
      AnomalyQueryInput,
      {
        controlId: "rcf-query",
        value: (_c = subtypeConfig.query) != null ? _c : "",
        onChange: handleQueryChange,
        streams: (_d = eventDefinition.config.streams) != null ? _d : [],
        eventDefinition
      }
    );
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !isFieldLocked && !isIlluminate && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-feature-field",
      name: "feature_field",
      label: "Feature Field",
      type: "text",
      required: true,
      value: subtypeConfig.feature_field,
      onChange: handleSubtypeInputChange,
      help: "Field whose aggregated value should be evaluated for anomalies."
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "rcf-feature-function" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Feature Function"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "rcf-feature-function-select",
      name: "feature_function",
      options: FEATURE_FUNCTION_OPTIONS,
      value: subtypeConfig.feature_function,
      clearable: false,
      menuPlacement: "bottom",
      onChange: (newValue) => handleFeatureFunctionChange(newValue)
    }
  )), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-category-field",
      name: "category_field",
      label: "Category Field",
      type: "text",
      value: (_e = subtypeConfig.category_field) != null ? _e : "",
      onChange: handleSubtypeInputChange,
      help: "Field to group aggregations by."
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-shingle-size",
      name: "shingle_size",
      label: "Shingle Size",
      type: "number",
      min: 1,
      step: 1,
      value: subtypeConfig.shingle_size,
      onChange: handleSubtypeInputChange,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-number-of-trees",
      name: "number_of_trees",
      label: "Number of Trees",
      type: "number",
      min: 1,
      step: 1,
      value: subtypeConfig.number_of_trees,
      onChange: handleSubtypeInputChange,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-sample-size",
      name: "sample_size",
      label: "Sample Size",
      type: "number",
      min: 1,
      step: 1,
      value: subtypeConfig.sample_size,
      onChange: handleSubtypeInputChange,
      required: true
    }
  )), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-grade-threshold",
      name: "grade_threshold",
      label: "Grade Threshold",
      type: "number",
      min: 0,
      max: 1,
      step: 0.01,
      value: subtypeConfig.grade_threshold,
      onChange: handleSubtypeInputChange,
      help: "Minimum anomaly grade (0-1) required to trigger an event.",
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-score-threshold",
      name: "score_threshold",
      label: "Score Threshold",
      type: "number",
      min: 0,
      step: 0.01,
      value: subtypeConfig.score_threshold,
      onChange: handleSubtypeInputChange,
      help: "Minimum anomaly score (>=0) required to trigger an event.",
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "rcf-create-non-anomaly-events-checkbox",
      type: "checkbox",
      name: "create_non_anomaly_events",
      label: "Create Debug Events",
      help: "Generate events for all detector runs, not only those that produce anomalies.",
      checked: (_f = subtypeConfig.create_non_anomaly_events) != null ? _f : false,
      onChange: handleSubtypeInputChange
    }
  ));
};
RcfEventForm.defaultSubtypeConfig = initialThresholdedRcfConfig;
RcfEventForm.defaultConfig = {
  streams: [],
  search_within_ms: 0,
  execute_every_ms: 5 * 60 * 1e3,
  _is_scheduled: true,
  use_cron_scheduling: false,
  cron_expression: null,
  cron_timezone: null,
  config: initialThresholdedRcfConfig
};
export default RcfEventForm;
